import type { InvoiceStatuses } from "@twa-dev/types";
import { deviceStorage, isTelegramEnvironment, telegramHaptics, tg } from "@/lib/telegram";
import { createInvoice, isAnyProductCode } from "@/api/payments";
import { THEME_GROUND, type AppearanceApi, type Theme } from "@/lib/theme";
import type {
  BackButtonApi,
  BuyOutcome,
  MainButtonApi,
  MainButtonState,
  Platform,
  PlatformProduct,
  PurchasesApi,
  RestoreOutcome,
  SharePayload,
} from "./types";

const noop = (): void => {};

// Identical to `useBackButton`'s effect body in `lib/telegram.ts` (the
// `visible` branch): register the click handler and show; cleanup offClicks
// and hides.
const backButton: BackButtonApi = {
  show(onClick: () => void): () => void {
    tg.BackButton.onClick(onClick);
    tg.BackButton.show();
    return () => {
      tg.BackButton.offClick(onClick);
      tg.BackButton.hide();
    };
  },
};

// Identical to `useMainButton`'s effect body in `lib/telegram.ts`.
const mainButton: MainButtonApi = {
  set(state: MainButtonState): () => void {
    const { text, onClick, visible, loading, disabled } = state;
    if (!visible) {
      tg.MainButton.hide();
      return noop;
    }
    tg.MainButton.setText(text);
    if (disabled) tg.MainButton.disable();
    else tg.MainButton.enable();
    if (loading) tg.MainButton.showProgress(false);
    else tg.MainButton.hideProgress();
    tg.MainButton.onClick(onClick);
    tg.MainButton.show();
    return () => {
      tg.MainButton.offClick(onClick);
      tg.MainButton.hide();
      tg.MainButton.hideProgress();
    };
  },
};

// Pins Telegram's own chrome to the app surface so it doesn't repaint on
// scroll and flicker the viewport — the concern `telegramInit.ts` used to
// serve with a hardcoded `#0a0a14`, now sourced from the resolved theme.
// Both setters are Bot API 6.1+; clients older than that throw, which must
// not take the theme runtime (or app init) down with them.
const appearance: AppearanceApi = {
  apply(theme: Theme): void {
    const ground = THEME_GROUND[theme];
    try {
      tg.setHeaderColor(ground);
    } catch {
      /* pre-6.1 client */
    }
    try {
      tg.setBackgroundColor(ground);
    } catch {
      /* pre-6.1 client */
    }
  },
};

// DUE-161 task 5: `purchases` wraps the SAME two primitives the
// `useBuyProduct` hook uses (`createInvoice` from `api/payments.ts`, then
// `tg.openInvoice`) rather than duplicating the invoice flow. The hook
// itself is untouched — it stays the Telegram paywall's UI entrypoint
// (state, `onPaid` callback incl. the `window.location.reload()` side
// effect, error surfacing) — this is a parallel, generic seam for
// callers going through `Platform.purchases` instead of the hook.
async function listProducts(codes: string[]): Promise<PlatformProduct[]> {
  // Telegram has no native per-product pricing metadata (no StoreKit
  // equivalent) — star prices come from `GET /api/payments/products`
  // (`useProducts()`), a separate, unchanged query. Echo the requested
  // codes so callers going through the generic Platform.purchases surface
  // still get a consistent shape across adapters.
  return codes.map((code) => ({ code }));
}

async function buy(code: string): Promise<BuyOutcome> {
  if (!isAnyProductCode(code)) {
    // Mirrors ios.ts's posture for an unmapped code: not a reachable path
    // from real callers (every code the paywall offers is a real
    // AnyProductCode) — fail loud via rejection rather than silently
    // swallowing a caller bug or resolving a fake "failed" outcome.
    throw new Error(`telegram: unknown product code "${code}"`);
  }
  const { invoice_link } = await createInvoice(code);
  return new Promise<BuyOutcome>((resolve) => {
    tg.openInvoice(invoice_link, (status: InvoiceStatuses) => {
      if (status === "paid") resolve({ status: "paid" });
      else if (status === "cancelled") resolve({ status: "cancelled" });
      else if (status === "failed") resolve({ status: "failed", reason: "invoice_failed" });
      // "pending" is transient — Telegram invokes the callback again once
      // a terminal status is reached, so the promise is left unsettled.
    });
  });
}

async function restore(): Promise<RestoreOutcome> {
  // Stars payments settle synchronously through the bot's payment webhook
  // — there is no client-side pending-entitlement queue to restore, unlike
  // StoreKit's `currentEntitlements()`. No-op success.
  return { restored: 0 };
}

const purchases: PurchasesApi = { listProducts, buy, restore };

async function share(payload: SharePayload): Promise<void> {
  // Telegram surfaces keep using ShareSheet (components/ShareSheet.tsx →
  // lib/shareMethods.ts `dispatchShare`) directly for the two-option share
  // flow (inline / story) — unchanged. This method exists only so
  // iOS-generic call sites going through the Platform abstraction have a
  // Telegram-side implementation. Guard on isTelegramEnvironment(): this
  // adapter also serves as the "web" (non-Telegram) fallback, where
  // switchInlineQuery has no inline-mode host to receive it.
  if (!isTelegramEnvironment()) return;
  tg.switchInlineQuery(payload.text ?? "", ["users"]);
}

export function createTelegramPlatform(): Platform {
  return {
    // DUE-180 pre-merge fix: this adapter also serves as the plain-browser
    // ("web") fallback — `index.ts` routes both "telegram" and "web"
    // `detectPlatformName()` results here (see the comment there). A
    // hardcoded "telegram" name was wrong for the web case: it fed
    // `X-Duetto-Platform` and analytics platform labels with the wrong
    // value for every non-Telegram, non-iOS browser session.
    name: isTelegramEnvironment() ? "telegram" : "web",
    haptics: telegramHaptics,
    // DUE-196: both channels used to point at `cloudStorage`, i.e. Telegram
    // CloudStorage — Telegram's servers. That put the onboarding draft
    // (name, birth date/time, city, coordinates) and the session envelope
    // (access JWT + profile) outside the EEA processing described in the
    // Privacy Policy. Both are device-local now; the one deliberate cloud
    // consumer left imports `cloudStorage` directly (see its docstring).
    storage: deviceStorage,
    secureStorage: deviceStorage,
    backButton,
    mainButton,
    appearance,
    share,
    openLink(url: string): void {
      tg.openLink(url);
    },
    close(): void {
      tg.close();
    },
    supportsPurchases: true,
    purchases,
  };
}
